/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: prop-types library.
import _ from 'underscore' // Npm: underscore.js library.
import { useLazyQuery, useQuery } from '@apollo/client' // Npm: Apollo client.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useHistory } from 'react-router-dom' // Npm: React router dom.
import { Chip } from "@mui/material";
import { HiArrowPath, HiArrowUturnDown } from 'react-icons/hi2' // Npm: react-icons library.
import { DataGrid, GridFooter } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DateRangePicker from 'components/DateRangePicker'
import TableSpinner from 'components/TableSpinner'
import { MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import CustomerMarginReportQuery from './__query__/index.customer.marginReport.query'
import CustomerAccountReadQuery from './__query__/index.customerAccount.read.query'


/*
 * OBJECTS
 */
const Index = ({ passOn, PassOnUpdate, activeCustomer }) => {
  const _historyRef = useHistory()

  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: passOn?.startDate ?? Object.DateRangeProvider()?.startDate,
      endDate: passOn?.endDate ?? Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [customerAccountId, setCustomerAccountId] = React.useState(void 0)
  const _customerAccountIdRef = React.useRef(void 0)
  const [smsRead, setSmsRead] = React.useState([])
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [refreshTrigger, setRefreshTrigger] = React.useState(0)
  const _companyNameRef = React.useRef(passOn?.companyName || '')

  const [QueryCustomerMarginReport, QueryCustomerMarginReportResponse] = useLazyQuery(CustomerMarginReportQuery, {
    'variables': {
      'customerId': passOn?.customerId || activeCustomer?.customerId,
      'startDate': dateRange?.selection.startDate,
      'endDate': dateRange?.selection.endDate
    }
  })
  const _QueryCustomerAccountRead = useQuery(CustomerAccountReadQuery, {
    'variables': { 'customerId': passOn?.customerId || activeCustomer?.customerId },
    'fetchPolicy': Object.React.App.fetchPolicy,
  })
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;

  
  React.useEffect(() => {
    if (passOn?.companyName) {
      _companyNameRef.current = passOn.companyName
    }
  }, [passOn?.companyName])


  // Event handler
  React.useEffect(() => {
    const _Async = async () => {
      const id = customerAccountId?.split('(')[1]?.split(')')?.[0]
      const { data, error } = await QueryCustomerMarginReport({
        'variables': {
          'customerId': passOn?.customerId || activeCustomer?.customerId,  // fallback
          'startDate': dateRange.selection.startDate,
          'endDate': dateRange.selection.endDate
        },
        'fetchPolicy': Object.React.App.fetchPolicy,
      })

      if (error instanceof Error) throw error

      // Const assignment.
      const _report = _.compact(Object.entries(data?.CustomerMarginReport?._report ?? {}))

      // If data is not empty then update it.
      if (0 < _report?.length) {
        // Const assignment.
        const _totalsumup = {}

        // Merge all by country names.
        for (const [__, value] of _.compact(Object.values(_report))) {
          // Break by countryName.
          for (const [key, value2] of Object.entries(value)) {
            // If country name is not in the list then add it.
            if (!_totalsumup[key]) _totalsumup[key] = {
              'totalsmscount': 0,
              'totaldelivered': 0,
              'totalfailed': 0,
              'dlrPercentage': 0,
              'revenue': 0,
              'cost': 0,
              'margin': 0
            }

            if (__ == id || _.isEmpty(id)) {
              _totalsumup[key].totalsmscount += (value2?.totalsmscount ?? 0)
              _totalsumup[key].totaldelivered += (value2?.totaldelivered ?? 0)
              _totalsumup[key].totalfailed += (value2?.totalfailed ?? 0)
              _totalsumup[key].dlrPercentage = _totalsumup[key].totalsmscount > 0
                ? ((_totalsumup[key].totaldelivered / _totalsumup[key].totalsmscount) * 100).toFixed(2)
                : 0;
              _totalsumup[key].revenue += value2?.revenue
              _totalsumup[key].cost += value2?.cost
              _totalsumup[key].margin += value2?.margin
            }
          }
        }

        // Update the state.
        setSmsRead(Object.entries(_totalsumup))
      } else setSmsRead([])
    }; _Async().catch(i => { throw i })
  }, [dateRange, passOn, customerAccountId, activeCustomer, refreshTrigger])

  // // Handle country click
  const _handleCountryClick = (row) => {
    const _smppName = _customerAccountIdRef.current?.split('(')?.[0]?.trim()  //  reference to customer account select value

    PassOnUpdate({
      customerId: passOn?.customerId || activeCustomer?.customerId,
      companyName: _companyNameRef.current || activeCustomer?.companyName,
      currency: passOn?.currency || activeCustomer?.currency,
      countryName: row.countryName,
      customerAccountSmppName: _smppName,
      startDate: dateRange.selection.startDate,
      endDate: dateRange.selection.endDate,
      _navigatedToMessage: true,
    });
    _historyRef.push('/reports/message')
  }
  // Total row values 
  const _totalRow = React.useMemo(() => {
    const _marginTotals = QueryCustomerMarginReportResponse?.data?.CustomerMarginReport?.marginTotals

    return {
      'revenue': parseFloat(_marginTotals?.totalRevenue ?? 0).toFixed(2),
      'cost': parseFloat(_marginTotals?.totalCost ?? 0).toFixed(2),
      'margin': parseFloat(_marginTotals?.totalMargin ?? 0).toFixed(2)
    }
  }, [QueryCustomerMarginReportResponse?.data])

  // Data assignment for DataGrid
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 80,
        sortable: false,
        filterable: false,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        hide: true,
      },
      {
        field: "countryName",
        headerName: "Country Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        renderCell: ({ row }) => (
          <a
            onClick={() => _handleCountryClick(row)}
            style={{
              color: "#6366F1",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {row.countryName}
          </a>
        ),
      },
      {
        field: "totalsmscount",
        headerName: "Total SMS",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "totaldelivered",
        headerName: "Delivered",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "totalfailed",
        headerName: "Failed",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "dlrPercentage",
        headerName: "DLR%",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        renderCell: ({ value }) => {
          const numeric = parseFloat(value);
          const bg =
            numeric >= 85 ? "#22c55e" : numeric >= 50 ? "#f59e0b" : "#ef4444"; 

          return (
            <Chip
              label={value ?? "-"}
              size="small"
              sx={{
                backgroundColor: `${bg} !important`,
                color: "white !important",
                fontWeight: 500,
                fontSize: "11px",
              }}
            />
          );
        },
      },
      {
        field: "revenue",
        headerName: "Revenue",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "cost",
        headerName: "Cost",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "margin",
        headerName: "Margin",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
      },
    ],
    [],
  );

  const rows = React.useMemo(() => _.compact(smsRead?.map((item, index) => item?.[0] ? ({
    'id': item?.[0],
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'countryName': item?.[0] ?? '-',
    'totalsmscount': item?.[1]?.totalsmscount ?? '-',
    'totaldelivered': item?.[1]?.totaldelivered ?? '-',
    'totalfailed': item?.[1]?.totalfailed ?? '-',
    'dlrPercentage': item?.[1]?.dlrPercentage != null ? `${item[1].dlrPercentage}%` : '-',
    'revenue': parseFloat(item?.[1]?.revenue ?? 0)?.toFixed?.(2) ?? '-',
    'cost': parseFloat(item?.[1]?.cost ?? 0)?.toFixed?.(2) ?? '-',
    'margin': parseFloat(item?.[1]?.margin ?? 0)?.toFixed?.(2) ?? '-'
  }) : null)), [smsRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className="reportsCustomer base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" alignItems="center" gap="22px">
            <Flex
              flex={_isCurrentViewMobile ? 1 : 0.6}
              gap="12px"
              flexDir="column"
            >
              <Text
                as="h3"
                fontSize={17}
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="brand.500"
                gap="6px"
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile
                  ? "Filter Rows"
                  : "Filter Rows based on Date"}
                .
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w="100%"
                bg="purple.50"
                color="purple.600"
                fontSize="13px"
                height="max-content"
                gap="6px"
                setSelectDate={setDateRange}
              />
            </Flex>
            <Flex
              flex={_isCurrentViewMobile ? 1 : 0.4}
              justify={_isCurrentViewMobile ? "flex-start" : "end"}
            >
              <Flex
                gap="12px"
                flexDir={{ base: "column", md: "row" }}
                flexWrap="wrap"
                w={{ base: "100%", md: "auto" }}
              >
                <MemoizedSelect
                  w={{ base: "100%", md: "240px" }}
                  name="customerId"
                  placeholder="Select Customer Account"
                  bg="purple.50"
                  color="purple"
                  data={customerAccountId}
                  options={(
                    _QueryCustomerAccountRead?.data?.CustomerAccountRead ?? []
                  )?.map((j) =>
                    j.displayName ? `${j.displayName} (${j.id})` : void 0,
                  )}
                  onChange={(e) => {
                    setCustomerAccountId(e.target?.value);
                    _customerAccountIdRef.current = e.target?.value; // update ref on change for later use in click event
                  }}
                />
                <Button
                  borderRadius="12px"
                  fontWeight="400"
                  maxW={{ base: "100%", md: "250px" }}
                  w={{ base: "100%", md: "auto" }}
                  bg="purple.50"
                  color="purple"
                  _hover={{ bg: "purple.800", color: "white" }}
                  onClick={() => {
                    _QueryCustomerAccountRead.refetch({
                      customerId:
                        passOn?.customerId || activeCustomer?.customerId,
                    });
                    setRefreshTrigger((prev) => prev + 1);
                  }}
                  isDisabled={
                    _QueryCustomerAccountRead?.loading ||
                    QueryCustomerMarginReportResponse?.loading
                  }
                  leftIcon={<HiArrowPath />}
                >
                  Refresh
                </Button>
              </Flex>
            </Flex>
          </Flex>

          <Flex
            position="sticky"
            top="0"
            width="100%"
            height="calc(100vh - 280px)"
            minHeight="400px"
            flexDirection="column"
            sx={{
              "& .MuiDataGrid-root": {
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                height: "100%",
              },
              "& .MuiDataGrid-main": {
                overflowY: "auto",
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={48}
              paginationMode="client"
              disableColumnFilter
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
              slots={{
                loadingOverlay: () => (
                  <TableSpinner
                    isLoading={true}
                    isEmpty={false}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noResultsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noRowsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                footer: () => (
                  <>
                    {_isCurrentViewMobile ? (
                      <Flex
                        w="100%"
                        border="1px solid"
                        borderColor="#D8E3FC"
                        px="12px"
                        py="10px"
                        gap="14px"
                        flexWrap="nowrap"
                        justify="flex-start"
                        align="center"
                        overflowX="auto"
                      >
                        <Text
                          fontWeight={700}
                          color="brand.500"
                          fontSize="13px"
                          whiteSpace="nowrap"
                        >
                          Total
                        </Text>
                        <Text
                          fontWeight={600}
                          fontSize="13px"
                          whiteSpace="nowrap"
                        >
                          <b>Revenue:</b> {_totalRow.revenue}
                        </Text>
                        <Text
                          fontWeight={600}
                          fontSize="13px"
                          whiteSpace="nowrap"
                        >
                          <b>Cost:</b> {_totalRow.cost}
                        </Text>
                        <Text
                          fontWeight={600}
                          fontSize="13px"
                          whiteSpace="nowrap"
                        >
                          <b>Margin:</b> {_totalRow.margin}
                        </Text>
                      </Flex>
                    ) : (
                      <Flex
                        w="100%"
                        borderBottom="1px solid"
                        borderLeft="1px solid"
                        borderRight="1px solid"
                        borderColor="#D8E3FC"
                        bg="white"
                        px="16px"
                        height="58px"
                        alignItems="center"
                      >
                        <Flex w="80px" justify="center">
                          <Text
                            fontWeight={700}
                            color="brand.500"
                            fontSize="14px"
                          ></Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text
                            fontWeight={700}
                            color="brand.500"
                            fontSize="14px"
                          >
                            Total
                          </Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text
                            fontWeight={700}
                            color="brand.500"
                            fontSize="14px"
                          ></Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text
                            fontWeight={700}
                            color="brand.500"
                            fontSize="14px"
                          ></Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text
                            fontWeight={700}
                            color="brand.500"
                            fontSize="14px"
                          ></Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text
                            fontWeight={700}
                            color="brand.500"
                            fontSize="14px"
                          ></Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text fontWeight={600} fontSize="14px">
                            {_totalRow.revenue}
                          </Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text fontWeight={600} fontSize="14px">
                            {_totalRow.cost}
                          </Text>
                        </Flex>
                        <Flex flex={1} justify="center">
                          <Text fontWeight={600} fontSize="14px">
                            {_totalRow.margin}
                          </Text>
                        </Flex>
                      </Flex>
                    )}
                    <GridFooter />
                  </>
                ),
              }}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              loading={QueryCustomerMarginReportResponse.loading}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({
  'passOn': __state.PassOn,
  'activeCustomer': __state.ActiveCustomer
})
const _MapDispatchToProps = __dispatch => ({
  'PassOnUpdate': u => __dispatch({ 'type': 'PASS_ON_UPDATE', 'PassOn': u })
})


/*
 * EXPORT
 */
export default connect(_MapStateToProps, _MapDispatchToProps)(Index)
